import { useState } from 'react';
import { ComplianceTab } from './types';
import ComplianceHeader from './components/ComplianceHeader';
import ComplianceHero from './components/ComplianceHero';
import GovernanceMaturity from './components/GovernanceMaturity';
import GovernancePillars from './components/GovernancePillars';
import ComplianceTabs from './components/ComplianceTabs';
import DeliverablesCenter from './components/DeliverablesCenter';
import GovernanceValue from './components/GovernanceValue';
import RiskCards from './components/RiskCards';
import SolutionFlow from './components/SolutionFlow';
import DeliverablesSection from './components/DeliverablesSection';
import UniversalCapabilities from './components/UniversalCapabilities';
import BoundaryStatement from './components/BoundaryStatement';
import ComplianceCTA from './components/ComplianceCTA';
import Footer from '../home/components/Footer';
import CapExampleModal from '../capabilities/components/CapExampleModal';
import CapConsultationDrawer from '../capabilities/components/CapConsultationDrawer';

export default function CompliancePage() {
  const [activeTab, setActiveTab] = useState<ComplianceTab>('customs');
  const [exampleOpen, setExampleOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 关务合规数据
  const customsRisks = [
    {
      icon: 'ri-error-warning-line',
      title: '要素填错/缺失 → 退单反复',
      desc: '申报要素不完整或错误，导致海关退单，反复修改浪费时间',
    },
    {
      icon: 'ri-file-damage-line',
      title: '单证散落 → 查验/抽查拿不出',
      desc: '单证分散在不同系统和文件夹，查验时找不到关键材料',
    },
    {
      icon: 'ri-links-line',
      title: '订单/出库/物流/报关对应关系说不清',
      desc: '各环节数据孤立，无法快速证明业务链路的完整性',
    },
  ];

  const customsSteps = [
    {
      icon: 'ri-folder-received-line',
      title: '资料归集',
      desc: '自动汇总订单、出库、物流、报关等关键单证',
    },
    {
      icon: 'ri-shield-check-line',
      title: '提交前校验',
      desc: '缺项、异常、逻辑冲突提前提示，减少退单与返工',
    },
    {
      icon: 'ri-send-plane-line',
      title: '直连提交/回执回传',
      desc: '每次提交/退单原因都记录在同一票据下',
    },
    {
      icon: 'ri-archive-line',
      title: '单证归档包',
      desc: '按批次/票号/主体一键导出，查验时"拿得出"',
    },
  ];

  const customsDeliverables = [
    { text: '缺资料清单（按票/批次）', checked: true },
    { text: '校验报告（提交前）', checked: true },
    { text: '回执与退单原因记录', checked: true },
    { text: '归档包目录（可下载）', checked: true },
  ];

  // 税务合规数据
  const taxRisks = [
    {
      icon: 'ri-git-branch-line',
      title: '数据穿透对账',
      desc: '平台流水、收款、报关、开票口径对不上 → 最怕被追问/补税风险',
    },
    {
      icon: 'ri-file-search-line',
      title: '9810"预退易、核销难"',
      desc: '材料关系不清、版本混乱 → 核销卡住反复补',
    },
    {
      icon: 'ri-question-line',
      title: '免税口径不统一',
      desc: '材料留存/链路不完整 → 合规解释成本高',
    },
  ];

  const taxSteps = [
    {
      icon: 'ri-git-merge-line',
      title: '三流自动对齐',
      desc: '不一致直接标红留痕，让风险暴露在系统里',
    },
    {
      icon: 'ri-route-line',
      title: '核销链路固化',
      desc: '把"关系"固化为系统字段，不靠人肉拼资料',
    },
    {
      icon: 'ri-archive-drawer-line',
      title: '单证归档可追溯',
      desc: '每票材料、版本、处理记录可回溯',
    },
    {
      icon: 'ri-file-list-3-line',
      title: '口径统一输出',
      desc: '按标准模板生成合规报告，减少解释成本',
    },
  ];

  const taxDeliverables = [
    { text: '三流差异留痕报告', checked: true },
    { text: '核销材料关系图', checked: true },
    { text: '版本记录与变更日志', checked: true },
    { text: '合规口径报告（可导出）', checked: true },
  ];

  // 外汇合规数据
  const forexRisks = [
    {
      icon: 'ri-exchange-line',
      title: '收款与出口业务对应不清',
      desc: '收款拆分/多笔回款难解释，银行/外管局要求说明',
    },
    {
      icon: 'ri-file-copy-line',
      title: '材料分散',
      desc: '合同、发票、报关、物流、收款凭证不在一个地方',
    },
    {
      icon: 'ri-team-line',
      title: '内部协同难',
      desc: '财务/关务/业务各管一段，口径对不上',
    },
  ];

  const forexSteps = [
    {
      icon: 'ri-links-line',
      title: '证据链归集',
      desc: '按票号/批次把收款凭证与出口单证关联归档',
    },
    {
      icon: 'ri-shield-user-line',
      title: '权限与留痕',
      desc: '财务/关务/运营分角色查看与导出，操作有记录',
    },
    {
      icon: 'ri-file-text-line',
      title: '关联关系可视化',
      desc: '一笔收款对应哪些出口业务，一目了然',
    },
    {
      icon: 'ri-download-cloud-line',
      title: '归档包导出',
      desc: '含目录、凭证、关联关系，随时可提供给银行/外管',
    },
  ];

  const forexDeliverables = [
    { text: '收款关联记录（按票/批次）', checked: true },
    { text: '导出归档包（含目录）', checked: true },
    { text: '操作日志（谁导出、何时导出）', checked: true },
    { text: '关联关系可视化图表', checked: true },
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'customs':
        return {
          risks: customsRisks,
          steps: customsSteps,
          deliverables: customsDeliverables,
          highlight: '',
        };
      case 'tax':
        return {
          risks: taxRisks,
          steps: taxSteps,
          deliverables: taxDeliverables,
          highlight: '一票一链路：订单/平台 → 出库/海外仓 → 物流 → 收款 → 报关 → 发票/单证',
        };
      case 'forex':
        return {
          risks: forexRisks,
          steps: forexSteps,
          deliverables: forexDeliverables,
          highlight: '',
        };
      default:
        return {
          risks: customsRisks,
          steps: customsSteps,
          deliverables: customsDeliverables,
          highlight: '',
        };
    }
  };

  const currentData = getCurrentData();

  return (
    <div className="min-h-screen bg-white">
      <ComplianceHeader />
      <ComplianceHero />
      <GovernanceMaturity />
      <GovernancePillars />
      <ComplianceTabs activeTab={activeTab} onChange={setActiveTab} />
      <RiskCards risks={currentData.risks} />
      <SolutionFlow steps={currentData.steps} highlight={currentData.highlight} />
      <DeliverablesSection
        items={currentData.deliverables}
        onViewExample={() => setExampleOpen(true)}
      />
      <DeliverablesCenter />
      <GovernanceValue />
      <UniversalCapabilities />
      <BoundaryStatement />
      <ComplianceCTA onOpenDrawer={() => setDrawerOpen(true)} activeTab={activeTab} />
      <Footer />
      <CapExampleModal
        open={exampleOpen}
        activeType="三流差异留痕"
        onClose={() => setExampleOpen(false)}
        onChangeType={() => {}}
      />
      <CapConsultationDrawer
        activeTab="9810"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        defaultNote={`我想咨询${activeTab === 'customs' ? '关务' : activeTab === 'tax' ? '税务' : '外汇'}合规治理方案`}
        defaultNeedTax={false}
      />
    </div>
  );
}